import { useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { useLanguage } from '../../contexts/language';
import { usePatientRecord } from '../../hooks/patient-record';

import { LoadFailed } from '../load-failed';
import { PatientHeader } from '../patient-header';
import { PatientTestRow } from '../patient-test-row';
import { SearchField } from '../search-field';

import styles from './style.module.css';

// A patient's Tests tab (C35): every test assigned to them, by anyone,
// searchable by name and grouped by status — To review, Not submitted,
// Reviewed — newest first inside each group. A test waiting for the patient
// is Not submitted however far they are: drafts stay with the patient (O11).
// Each submitted attempt is its own row and opens its review; reviewed means
// reviewed by the signed-in clinician.
export function PatientTests() {
  const { text } = useLanguage();
  const { patientId } = useParams();
  const { data, error, retry, reload } = usePatientRecord(patientId);
  const [ search, setSearch ] = useState('');

  const groups = useMemo(function() {
    if (data === undefined) {
      return [];
    }

    const wanted = search.trim().toLowerCase();
    const shown = data.patientAssignments.filter(function({ questionnaire }) {
      return questionnaire.name.toLowerCase().includes(wanted);
    });

    const waiting = shown.filter(function({ open }) {
      return open === true;
    }).map(function(assignment) {
      return <PatientTestRow key={ assignment.id } assignment={ assignment } onChange={ reload }/>;
    });

    const attempts = shown.flatMap(function(assignment) {
      return assignment.attempts.map(function(attempt) {
        return { assignment, attempt };
      });
    }).sort(function(one, other) {
      return other.attempt.submittedAt.localeCompare(one.attempt.submittedAt);
    });

    const rowOf = function({ assignment, attempt }) {
      return <PatientTestRow key={ attempt.id } patientId={ patientId } assignment={ assignment } attempt={ attempt }/>;
    };

    const toReview = attempts.filter(function({ attempt }) {
      return attempt.reviewedAt == null;
    }).map(rowOf);

    const reviewed = attempts.filter(function({ attempt }) {
      return attempt.reviewedAt != null;
    }).map(rowOf);

    return [
      { id: 'to-review', label: text.toReview, rows: toReview },
      { id: 'not-submitted', label: text.notSubmitted, rows: waiting },
      { id: 'reviewed', label: text.reviewed, rows: reviewed }
    ].filter(function({ rows }) {
      return rows.length > 0;
    });
  }, [ data, search, reload, text, patientId ]);

  if (error != null) {
    return <LoadFailed onRetry={ retry }/>;
  }

  if (data === undefined) {
    return null;
  }

  let list = <p className={ styles.empty }>{ text.noTestsFound }</p>;

  if (groups.length > 0) {
    list = (
      <section className={ styles.list } aria-label={ text.assignedTests }>
        {
          groups.map(function({ id, label, rows }) {
            return (
              <div key={ id } className={ styles.group }>
                <h3 className={ styles.heading }>{ label } <span className={ styles.count }>· { rows.length }</span></h3>

                { rows }
              </div>
            );
          })
        }
      </section>
    );
  }

  return (
    <section className={ styles.page }>
      <PatientHeader patient={ data.patient }/>

      <div className={ styles.title }>
        <h2 className={ styles.tabTitle }>{ text.tests }</h2>

        <SearchField id='patient-tests-search' label={ text.searchTests } placeholder={ text.searchByName } value={ search } onChange={ setSearch }/>
      </div>

      { list }

      <p className={ styles.hint }>{ text.draftsStayWith.replace('{name}', data.patient.firstName) }</p>
    </section>
  );
}
